import { ApplicationRef, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { SnackbarMessageComponent } from '../components/snackbar/snackbar-message/snackbar-message.component';
import { Config } from '../models/config.model';
import { AuthenticationService } from './authentication.service';
import { ConfigService } from './config.service';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})
export class IdleService {

  private idleTimeout: number = 300; //5 min

  private idleConfigured: boolean = false;

  private config!: Config;
  private currentSnackbar!: MatSnackBarRef<SnackbarMessageComponent> | undefined;

  constructor(
    private idle: Idle,
    private keepalive: Keepalive,
    private configService: ConfigService,
    private snackbar: MatSnackBar,
    private router: Router,
    private authenticationService: AuthenticationService,
    private ref: ApplicationRef) {

    this.configService.config
      .subscribe(
        (config: Config | null) => {
          if (config !== null) {
            this.config = config;
            if (!this.idleConfigured) {
              this.configureIdle();
            }
          }
        }
      );
  }

  public configureIdle(): void {

    let idleTime: number = this.config.SessionTimeout;

    if (this.config.SessionTimeout > this.idleTimeout) {
      idleTime = this.config.SessionTimeout - this.idleTimeout;
    }

    // set idle parameters
    this.idle.setIdle(idleTime); // how long can they be inactive before considered idle, in seconds
    this.idle.setTimeout(this.idleTimeout); // how long can they be idle before considered timed out, in seconds    
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES); // provide sources that will "interrupt" aka provide events indicating the user is active

    // do something when the user becomes idle
    this.idle.onIdleStart
      .subscribe(
        () => {
          this.logIdleState('Idle start...');
        });

    // do something when the user is no longer idle
    this.idle.onIdleEnd
      .subscribe(
        () => {
          this.logIdleState('Idle canceled...');
          this.dismissSessionExpireSnackBar();
          this.keepalive.ping();
        });

    // do something when the user has timed out
    this.idle.onTimeout
      .subscribe(
        () => {
          this.logIdleState('Timed out - logging out...');

          this.dismissSessionExpireSnackBar();

          if (this.authenticationService.isLoggedIn) {
            this.authenticationService.logout();
          } else {
            this.router.navigateByUrl('/access/idle');
          }
        });

    // do something as the timeout countdown does its thing
    this.idle.onTimeoutWarning
      .subscribe(
        (seconds: number) => {

          let result: number = seconds % 60;
          if (result === 0) {
            let minutes = seconds / 60;
            this.openSessionExpireSnackBar(minutes);
          }

          let message = `${seconds} seconds left...`;
          this.logIdleState(message);

        });

    // set keepalive parameters, omit if not using keepalive
    this.keepalive.request('/api/config/ping')
    this.keepalive.interval(this.idleTimeout); // will ping at this interval while not idle, in seconds
    this.keepalive.onPing
      .subscribe(
        (value: any) => {
          this.logIdleState('Last ping');
        });

    this.idleConfigured = true;
    this.reset();
  }

  public stop() {

    if (this.currentSnackbar) {
      this.currentSnackbar.dismiss();
      this.currentSnackbar = undefined;
    }

    this.idle.stop();
    this.logIdleState('Idle watch stopped..');
  }

  public reset() {
    if (this.idleConfigured) {
      this.dismissSessionExpireSnackBar();
      this.idle.watch();
      this.logIdleState('Idle watch reset..');
    }
  }

  private openSessionExpireSnackBar(minutes: number) {

    let data = {
      message: `Din sesjon utløper om ${minutes} minutt${minutes > 1 ? 'er,' : ','} på grunn av inaktivitet`,
      type: 'warning'
    }
    if (this.currentSnackbar) {
      this.currentSnackbar.instance.message = data.message;
    }
    else {
      this.currentSnackbar = this.snackbar.openFromComponent(SnackbarMessageComponent, { data: data, duration: 0 });
    }
  }

  private dismissSessionExpireSnackBar() {
    if (this.currentSnackbar) {
      this.currentSnackbar.dismiss();
      this.currentSnackbar = undefined;
      this.ref.tick();
    }

  }

  private logIdleState(message: string): void {
    const d = new Date();
    console.log(`${d.toLocaleTimeString()} ${message}`);
  }


}
